/**
 * certification.ts — Teacher-Ready Output Certification Engine
 *
 * SECTION 8 of the Curriculum Rendering Spec.
 *
 * Runs a named suite of checks immediately before any export (print, PPTX, PDF).
 * Each check has a severity:
 *   block — export must not proceed; teacher sees an actionable message.
 *   warn  — teacher is informed but may proceed.
 *
 * Call runCertification(ctx) from every export path.
 * If report.passed is false, surface the blockers and halt.
 * Provide a bypass option that logs the override but allows export.
 *
 * NEVER add vague checks ("something might be wrong"). Every check must be:
 *   1. Deterministic — same inputs always produce the same result.
 *   2. Actionable — the failure message must tell the teacher exactly what to fix.
 *   3. Narrowly scoped — one rule per check, no bundled failures.
 */

import type { GeneratedLesson } from './lesson-engine';
import type { LessonBlueprint } from './lesson-blueprint';
import { lessonContentKey } from './lesson-blueprint';
import type { GeneratedActivitySheet, ActivitySection } from './activity-engine';
import type { GeneratedPresentation } from './presentation-engine';
import type { PresentationSlide } from './presentation-engine';
import type { GeneratedAssessment, AssessmentQuestion } from './assessment-engine';
import { checkBlockedResolutions } from './rendering-registry';

// ── Public types ───────────────────────────────────────────────────────────────

export type CertificationSeverity = 'block' | 'warn';

export type ResourceKind = 'activity-sheet' | 'presentation' | 'assessment';

/**
 * All data available to the certification engine at export time.
 * Populate every field you have; checks skip gracefully when context is absent.
 */
export interface CertificationContext {
  resource: ResourceKind;
  /** Activity Sheet resource (activity-sheet exports) */
  sheet?: GeneratedActivitySheet | null;
  /** Presentation resource (presentation exports) */
  presentation?: GeneratedPresentation | null;
  /** Visible-only slide array after hidden-slide filter (presentation exports) */
  visibleSlides?: PresentationSlide[];
  /** Assessment resource (assessment exports) */
  assessment?: GeneratedAssessment | null;
  /** Source lesson — enables cross-resource consistency checks */
  lesson?: GeneratedLesson | null;
  /** Structured blueprint extracted from the lesson */
  blueprint?: LessonBlueprint | null;
}

/** Result of a single certification check. */
export interface CheckResult {
  checkId: string;
  /** Human-readable name shown in the certification modal header */
  checkName: string;
  passed: boolean;
  severity: CertificationSeverity;
  /**
   * Message shown ONLY on failure. Describe: which check, which resource,
   * which section, and what the teacher should do about it.
   * Max ~160 characters for clean modal display.
   */
  message: string;
  /** Optional extended detail (section name, item list, etc.) */
  detail?: string;
}

/** Aggregated report returned by runCertification(). */
export interface CertificationReport {
  /**
   * true when no block-severity checks failed.
   * warn-only failures still produce passed:true — they do not block export.
   */
  passed: boolean;
  hasWarnings: boolean;
  results: CheckResult[];
  /** Subset of results where passed:false and severity:'block' */
  blockers: CheckResult[];
  /** Subset of results where passed:false and severity:'warn' */
  warnings: CheckResult[];
}

// ── Internal check type ────────────────────────────────────────────────────────

interface Check {
  id: string;
  name: string;
  severity: CertificationSeverity;
  /** The rule function. Returns undefined when the check doesn't apply. */
  run: (ctx: CertificationContext) => { passed: boolean; message?: string; detail?: string } | undefined;
}

// ── Helper utilities ────────────────────────────────────────────────────────────

function sectionProblems(sec: ActivitySection | undefined): number {
  return sec?.problems?.length ?? 0;
}

function visibleSections(sheet: GeneratedActivitySheet): ActivitySection[] {
  const all: (ActivitySection | undefined)[] = [
    sheet.warmUp, sheet.vocabularyActivity, sheet.directInstruction,
    sheet.guidedPractice, sheet.independentPractice,
    sheet.belowLevelPractice, sheet.onLevelPractice, sheet.aboveLevelPractice,
    sheet.challengeProblem, sheet.exitTicket,
  ];
  const hidden = new Set(sheet.hiddenSections ?? []);
  return all.filter((s): s is ActivitySection => !!s && !hidden.has(s.heading));
}

// ── Check registry ─────────────────────────────────────────────────────────────

const CHECKS: Check[] = [

  // ────────────────────────────────────────────────────────────────────────────
  // ACTIVITY SHEET checks
  // ────────────────────────────────────────────────────────────────────────────

  {
    id: 'SHEET_BLOCKED_REPRESENTATIONS',
    name: 'Required visuals available',
    severity: 'block',
    run({ sheet }) {
      if (!sheet) return undefined;
      const allProblems = [
        sheet.warmUp, sheet.vocabularyActivity, sheet.directInstruction,
        sheet.guidedPractice, sheet.independentPractice,
        sheet.belowLevelPractice, sheet.onLevelPractice, sheet.aboveLevelPractice,
        sheet.challengeProblem, sheet.exitTicket,
      ].flatMap(s => s?.problems ?? []);
      const blocked = checkBlockedResolutions(allProblems);
      if (blocked.length === 0) return { passed: true };
      const nums = blocked.map(b => `#${b.problemNumber}`).join(', ');
      return {
        passed: false,
        message: `Problem${blocked.length > 1 ? 's' : ''} ${nums} require a visual that is not yet available.`,
        detail: blocked[0].message,
      };
    },
  },

  {
    id: 'SHEET_HAS_LEARNING_TARGET',
    name: 'Learning target present',
    severity: 'warn',
    run({ sheet }) {
      if (!sheet) return undefined;
      if (!sheet.config.showLearningTarget) return { passed: true }; // deliberately hidden — OK
      const passed = (sheet.learningTarget ?? '').trim().length > 0;
      return {
        passed,
        message: 'The Learning Target is empty. Add a target before printing so students know the goal.',
      };
    },
  },

  {
    id: 'SHEET_NO_EMPTY_VISIBLE_SECTIONS',
    name: 'No empty visible sections',
    severity: 'block',
    run({ sheet }) {
      if (!sheet) return undefined;
      const visible = visibleSections(sheet);
      const empty = visible.filter(s => (s.problems?.length ?? 0) === 0);
      if (empty.length === 0) return { passed: true };
      const names = empty.map(s => s.heading).join(', ');
      return {
        passed: false,
        message: `Section${empty.length > 1 ? 's' : ''} "${names}" ${empty.length > 1 ? 'are' : 'is'} visible but contain no problems. Hide the section or regenerate.`,
      };
    },
  },

  {
    id: 'SHEET_MINIMUM_PROBLEMS',
    name: 'Minimum problem count',
    severity: 'block',
    run({ sheet }) {
      if (!sheet) return undefined;
      const visible = visibleSections(sheet);
      const total = visible.reduce((n, s) => n + (s.problems?.length ?? 0), 0);
      if (total >= 3) return { passed: true };
      return {
        passed: false,
        message: `Only ${total} problem${total !== 1 ? 's' : ''} across the entire worksheet — minimum is 3. Regenerate or unhide sections.`,
      };
    },
  },

  {
    id: 'SHEET_GP_COVERAGE',
    name: 'Guided Practice coverage',
    severity: 'warn',
    run({ sheet, blueprint }) {
      if (!sheet || !blueprint) return undefined;
      const bpCount = blueprint.guidedPractice.items.length;
      if (bpCount === 0) return { passed: true }; // blueprint has none — skip
      const sheetCount = sectionProblems(sheet.guidedPractice);
      if (sheetCount >= 1) return { passed: true };
      return {
        passed: false,
        message: `The lesson blueprint has ${bpCount} Guided Practice item${bpCount > 1 ? 's' : ''} but the activity sheet has none. Regenerate the sheet to restore alignment.`,
      };
    },
  },

  {
    id: 'SHEET_IP_COVERAGE',
    name: 'Independent Practice coverage',
    severity: 'warn',
    run({ sheet, blueprint }) {
      if (!sheet || !blueprint) return undefined;
      const bpCount = blueprint.independentPractice.items.length;
      if (bpCount === 0) return { passed: true };
      // Tiered sheets replace the IP slot — treat any tier as coverage
      const tieredTotal =
        sectionProblems(sheet.belowLevelPractice) +
        sectionProblems(sheet.onLevelPractice) +
        sectionProblems(sheet.aboveLevelPractice);
      const ipCount = sectionProblems(sheet.independentPractice) + tieredTotal;
      if (ipCount >= 1) return { passed: true };
      return {
        passed: false,
        message: `The lesson blueprint has ${bpCount} Independent Practice item${bpCount > 1 ? 's' : ''} but none appear on the activity sheet. Regenerate the sheet to restore alignment.`,
      };
    },
  },

  {
    id: 'SHEET_VOCAB_COVERAGE',
    name: 'Vocabulary section coverage',
    severity: 'warn',
    run({ sheet, blueprint }) {
      if (!sheet || !blueprint) return undefined;
      if (!sheet.config.includeVocabulary) return { passed: true }; // teacher opted out
      const vocabTerms = blueprint.vocabulary.length;
      if (vocabTerms === 0) return { passed: true };
      const sheetVocab = sectionProblems(sheet.vocabularyActivity);
      if (sheetVocab >= 1) return { passed: true };
      return {
        passed: false,
        message: `The lesson has ${vocabTerms} vocabulary term${vocabTerms > 1 ? 's' : ''} but the Vocabulary section is empty. Regenerate or disable the Vocabulary section.`,
      };
    },
  },

  {
    id: 'SHEET_EXIT_TICKET_PRESENT',
    name: 'Exit ticket present',
    severity: 'warn',
    run({ sheet, blueprint }) {
      if (!sheet || !blueprint) return undefined;
      if (!sheet.config.includeExitTicket) return { passed: true }; // teacher opted out
      const bpCount = blueprint.exitTicket.items.length;
      if (bpCount === 0) return { passed: true };
      const sheetCount = sectionProblems(sheet.exitTicket);
      if (sheetCount >= 1) return { passed: true };
      return {
        passed: false,
        message: 'The Exit Ticket section is included but empty. Add at least one question or disable the section.',
      };
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // PRESENTATION checks
  // ────────────────────────────────────────────────────────────────────────────

  {
    id: 'PRES_BLOCKED_SLIDES',
    name: 'Required slide visuals available',
    severity: 'block',
    run({ visibleSlides }) {
      if (!visibleSlides) return undefined;
      // Re-use resolveForHint / resolveWithFallback logic inline to avoid circular dep
      // The existing export gate in PresentationGenerator already does this check;
      // we promote it here so it appears in the unified certification report.
      // Implementation: check is handled separately in PresentationGenerator via
      // checkBlockedResolutions equivalent — return passed here to avoid double-blocking.
      // The per-slide check in PresentationGenerator is more accurate (uses resolveForHint
      // which is not exported from certification). Mark as pass-through.
      return { passed: true };
    },
  },

  {
    id: 'PRES_SLIDE_COUNT_MIN',
    name: 'Minimum slide count',
    severity: 'block',
    run({ visibleSlides, presentation }) {
      if (!visibleSlides && !presentation) return undefined;
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const count = slides.length;
      if (count >= 3) return { passed: true };
      return {
        passed: false,
        message: `Only ${count} visible slide${count !== 1 ? 's' : ''}. A presentation needs at least 3 slides to be classroom-ready. Regenerate to add content.`,
      };
    },
  },

  {
    id: 'PRES_SLIDE_COUNT_MAX',
    name: 'Slide count within range',
    severity: 'warn',
    run({ visibleSlides, presentation }) {
      if (!visibleSlides && !presentation) return undefined;
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const count = slides.length;
      if (count <= 45) return { passed: true };
      return {
        passed: false,
        message: `${count} visible slides is unusually long for a single lesson. Consider hiding slides or splitting into multiple presentations.`,
      };
    },
  },

  {
    id: 'PRES_HAS_CONTENT_SLIDES',
    name: 'Has instructional content slides',
    severity: 'block',
    run({ visibleSlides, presentation }) {
      if (!visibleSlides && !presentation) return undefined;
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const NON_FRAME_KINDS = new Set(['vocabulary', 'concept', 'practice', 'you-try', 'discuss', 'reveal', 'prediction', 'misconception', 'exit-ticket', 'engagement']);
      const contentSlides = slides.filter(s => NON_FRAME_KINDS.has(s.kind));
      if (contentSlides.length >= 1) return { passed: true };
      return {
        passed: false,
        message: 'The presentation has no instructional content slides — only framing slides (title, learning target, hook). Regenerate to add lesson content.',
      };
    },
  },

  {
    id: 'PRES_VOCAB_SLIDES',
    name: 'Vocabulary slides match blueprint',
    severity: 'warn',
    run({ visibleSlides, presentation, blueprint }) {
      if ((!visibleSlides && !presentation) || !blueprint) return undefined;
      if (blueprint.vocabulary.length === 0) return { passed: true };
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const hasVocabSlide = slides.some(s => s.kind === 'vocabulary');
      if (hasVocabSlide) return { passed: true };
      return {
        passed: false,
        message: `The lesson has ${blueprint.vocabulary.length} vocabulary term${blueprint.vocabulary.length > 1 ? 's' : ''} but no vocabulary slide is visible in the presentation. Add a vocabulary slide or unhide existing ones.`,
      };
    },
  },

  {
    id: 'PRES_PRACTICE_SLIDES',
    name: 'Practice slides match blueprint',
    severity: 'warn',
    run({ visibleSlides, presentation, blueprint }) {
      if ((!visibleSlides && !presentation) || !blueprint) return undefined;
      const gpCount = blueprint.guidedPractice.items.length;
      if (gpCount === 0) return { passed: true };
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const hasPractice = slides.some(s => s.kind === 'practice' || s.kind === 'you-try' || s.kind === 'discuss');
      if (hasPractice) return { passed: true };
      return {
        passed: false,
        message: `The lesson has ${gpCount} Guided Practice item${gpCount > 1 ? 's' : ''} but no practice slides are visible. Add practice slides so students have follow-along problems.`,
      };
    },
  },

  {
    id: 'PRES_NO_EMPTY_BODY_SLIDES',
    name: 'No content slides with empty body',
    severity: 'warn',
    run({ visibleSlides, presentation }) {
      if (!visibleSlides && !presentation) return undefined;
      const slides = visibleSlides ?? presentation?.slides ?? [];
      const NEEDS_BODY = new Set(['concept', 'practice', 'you-try', 'discuss', 'reveal', 'vocabulary', 'misconception', 'exit-ticket']);
      // A content slide is "empty" when bodyLines is absent or empty.
      // PresentationSlide carries all student-visible text in bodyLines.
      const empty = slides.filter(s =>
        NEEDS_BODY.has(s.kind) && (s.bodyLines ?? []).length === 0
      );
      if (empty.length === 0) return { passed: true };
      const label = empty[0].heading ?? `Slide ${empty[0].id}`;
      return {
        passed: false,
        message: `${empty.length} content slide${empty.length > 1 ? 's have' : ' has'} no body text. First empty: "${label}". Edit the slide or hide it before exporting.`,
      };
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // ASSESSMENT checks
  // ────────────────────────────────────────────────────────────────────────────

  {
    id: 'ASSESS_HAS_QUESTIONS',
    name: 'Assessment has questions',
    severity: 'block',
    run({ assessment }) {
      if (!assessment) return undefined;
      const count = assessment.questions?.length ?? 0;
      if (count >= 1) return { passed: true };
      return {
        passed: false,
        message: 'The assessment has no questions. Regenerate before printing.',
      };
    },
  },

  {
    id: 'ASSESS_TOTAL_POINTS_NONZERO',
    name: 'Total points assigned',
    severity: 'block',
    run({ assessment }) {
      if (!assessment) return undefined;
      if ((assessment.totalPoints ?? 0) > 0) return { passed: true };
      return {
        passed: false,
        message: 'Total points is 0. Every question must have a point value. Regenerate the assessment.',
      };
    },
  },

  {
    id: 'ASSESS_ANSWER_KEY_COMPLETE',
    name: 'Answer key complete',
    severity: 'block',
    run({ assessment }) {
      if (!assessment) return undefined;
      const qs: AssessmentQuestion[] = assessment.questions ?? [];
      // AssessmentQuestion.answer holds the correct answer for all question kinds
      // (letter for MC, text for short-answer/CR, etc.). Any blank answer is a gap.
      const missing = qs.filter(q => !(q.answer ?? '').trim());
      if (missing.length === 0) return { passed: true };
      const nums = missing.map((_, i) => i + 1).slice(0, 5).join(', ');
      return {
        passed: false,
        message: `${missing.length} question${missing.length > 1 ? 's are' : ' is'} missing an answer in the answer key (Q${nums}${missing.length > 5 ? ', …' : ''}). Regenerate to complete the key.`,
      };
    },
  },

  {
    id: 'ASSESS_SYNC_CURRENT',
    name: 'Assessment is synchronized with lesson',
    severity: 'warn',
    run({ assessment, lesson }) {
      if (!assessment || !lesson) return undefined;
      if (!assessment.syncKey) return { passed: true }; // pre-sync-key save — skip
      const currentKey = lessonContentKey(lesson);
      if (assessment.syncKey === currentKey) return { passed: true };
      return {
        passed: false,
        message: 'The source lesson was updated after this assessment was generated. Export may be out of sync — consider regenerating the assessment.',
      };
    },
  },

  {
    id: 'ASSESS_NO_NOVEL_VOCABULARY',
    name: 'No novel vocabulary in assessment',
    severity: 'warn',
    run({ assessment, blueprint }) {
      if (!assessment || !blueprint) return undefined;
      if (blueprint.vocabulary.length === 0) return { passed: true };
      // Build a normalized set of lesson terms
      const lessonTerms = new Set(blueprint.vocabulary.map(v => v.term.toLowerCase().trim()));
      const qs: AssessmentQuestion[] = assessment.questions ?? [];
      // Look for terms in question text that are clearly vocabulary-like (proper-noun or 3+ syllable)
      // but NOT in the lesson vocabulary. This is heuristic — only flag obvious new terms.
      // Strategy: check if any question's prompt references a term that is longer than 6 chars,
      // starts with a capital, and does not appear in any lesson vocabulary or the lesson title.
      const lessonTitle = (assessment.skillToTeach ?? assessment.title ?? '').toLowerCase();
      const novelTerms: string[] = [];
      for (const q of qs) {
        const words = (q.stem ?? '').match(/\b[A-Z][a-z]{5,}\b/g) ?? [];
        for (const w of words) {
          const low = w.toLowerCase();
          if (!lessonTerms.has(low) && !lessonTitle.includes(low) && !novelTerms.includes(w)) {
            // Accept up to 3 potential novel terms to avoid false-positive spam
            if (novelTerms.length < 3) novelTerms.push(w);
          }
        }
      }
      // Only warn if we found meaningful novel terms AND the lesson has vocab
      if (novelTerms.length === 0) return { passed: true };
      // This is a warn-only (not a blocker) — novel vocab may be intentional
      return {
        passed: false,
        message: `Assessment may introduce terms not in the lesson vocabulary: ${novelTerms.join(', ')}. Verify these are accessible to students before printing.`,
      };
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // CROSS-RESOURCE checks (require both resource + blueprint)
  // ────────────────────────────────────────────────────────────────────────────

  {
    id: 'CROSS_BLUEPRINT_COMPLETENESS',
    name: 'Lesson blueprint is complete',
    severity: 'block',
    run({ blueprint }) {
      if (!blueprint) return { passed: true }; // no blueprint — skip (legacy lesson)
      const coreSections = [
        'guidedPractice', 'independentPractice', 'exitTicket',
      ] as const;
      const empty = coreSections.filter(k => blueprint[k].items.length === 0);
      if (empty.length === 0) return { passed: true };
      const names = empty.map(k => ({
        guidedPractice: 'Guided Practice',
        independentPractice: 'Independent Practice',
        exitTicket: 'Exit Ticket',
      } as Record<string, string>)[k]).join(', ');
      return {
        passed: false,
        message: `The lesson blueprint is missing content for: ${names}. Regenerate the source lesson before exporting companion resources.`,
      };
    },
  },
];

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Run the full certification suite for the given export context.
 * Returns a CertificationReport that the UI uses to gate or warn.
 */
export function runCertification(ctx: CertificationContext): CertificationReport {
  const results: CheckResult[] = [];

  for (const check of CHECKS) {
    let outcome: ReturnType<Check['run']>;
    try {
      outcome = check.run(ctx);
    } catch {
      // A thrown error in a check is itself a warning — don't crash the export
      outcome = {
        passed: false,
        message: `Certification check "${check.name}" threw an unexpected error. Bypassing this check.`,
      };
    }
    if (outcome === undefined) continue; // check doesn't apply to this resource

    results.push({
      checkId: check.id,
      checkName: check.name,
      passed: outcome.passed,
      severity: check.severity,
      message: outcome.passed ? '' : (outcome.message ?? `Check "${check.name}" failed.`),
      detail: outcome.detail,
    });
  }

  const blockers = results.filter(r => !r.passed && r.severity === 'block');
  const warnings = results.filter(r => !r.passed && r.severity === 'warn');

  return {
    passed: blockers.length === 0,
    hasWarnings: warnings.length > 0,
    results,
    blockers,
    warnings,
  };
}

/**
 * Convenience: returns true when the resource passes all blocking checks.
 * Use when you only need a go/no-go signal and don't need to display messages.
 */
export function isCertified(ctx: CertificationContext): boolean {
  return runCertification(ctx).passed;
}
